import { For, Show, createSignal, onMount } from "solid-js";
import { STATUS_LABELS, formatDate, formatDay, type AdminApi, type SchoolAction, type SchoolDetail } from "./api";

type Pending = { kind: SchoolAction; memberId?: string; memberName?: string };

const TITLES: Record<SchoolAction, string> = {
  members: "Lehrkraft zur Schule hinzufügen",
  "members/remove": "Mitglied aus der Schule entfernen",
  "extend-trial": "Test verlängern",
  "manual-license": "Manuelle Lizenz setzen",
  suspend: "Schule sperren",
  reactivate: "Schule entsperren"
};

const ROLE_LABELS = { owner: "Schulleitung", teacher: "Lehrkraft" } as const;

export function SchoolDetailView(props: { api: AdminApi; guard: <T>(work: () => Promise<T>) => Promise<T>; schoolId: string; onBack: () => void }) {
  const [detail, setDetail] = createSignal<SchoolDetail | null>(null);
  const [error, setError] = createSignal("");
  const [pending, setPending] = createSignal<Pending | null>(null);
  const [dialogError, setDialogError] = createSignal("");
  const [saving, setSaving] = createSignal(false);

  const load = async () => {
    try {
      setDetail(await props.guard(() => props.api.school(props.schoolId)));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    }
  };
  onMount(() => void load());

  const open = (next: Pending) => { setPending(next); setDialogError(""); };

  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    const current = pending();
    if (!current) return;
    const values = new FormData(event.currentTarget as HTMLFormElement);
    const field = (name: string) => String(values.get(name) ?? "").trim();
    const body: Record<string, unknown> = { reason: field("reason") };
    if (current.kind === "extend-trial" || current.kind === "manual-license") body.endsOn = field("endsOn") || undefined;
    if (current.kind === "members") { body.email = field("email"); body.role = field("role"); }
    if (current.kind === "members/remove") body.userId = current.memberId;
    setSaving(true);
    setDialogError("");
    try {
      await props.guard(() => props.api.schoolAction(props.schoolId, current.kind, body));
      setPending(null);
      await load();
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : "Aktion fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div class="adm-head">
        <button class="adm-btn ghost small" type="button" onClick={props.onBack}>← Alle Schulen</button>
        <Show when={detail()}>{(current) => <h2>{current().school.name}</h2>}</Show>
      </div>
      <Show when={error()}><p class="adm-error" role="alert">{error()}</p></Show>

      <Show when={detail()}>
        {(current) => {
          const school = () => current().school;
          const frozen = () => school().storedStatus === "suspended" || school().storedStatus === "deleted";
          return (
            <>
              <div class="adm-card">
                <dl class="adm-facts">
                  <dt>Status</dt><dd><span class={`adm-pill ${school().status}`}>{STATUS_LABELS[school().status]}</span>{school().storedStatus !== school().status ? ` (gespeichert: ${STATUS_LABELS[school().storedStatus]})` : ""}</dd>
                  <dt>Laufzeit</dt>
                  <dd>{school().licenseKind === "manual"
                    ? `Manuelle Lizenz, ${school().licenseEndsAt ? `bis ${formatDay(school().licenseEndsAt)}` : "ohne Ende"}`
                    : school().trialEndsAt ? `Test von ${formatDay(school().trialStartedAt)} bis ${formatDay(school().trialEndsAt)}` : "–"}</dd>
                  <dt>Schulform</dt><dd>{school().schoolType ?? "–"}</dd>
                  <dt>Schulnummer</dt><dd>{school().schoolNumber ?? "–"}</dd>
                  <dt>Adresse</dt><dd>{[school().street, [school().postalCode, school().city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "–"}</dd>
                  <dt>Angelegt</dt><dd>{formatDate(school().createdAt)}</dd>
                </dl>
                <div class="adm-actions" style={{ "margin-top": "16px" }}>
                  <Show when={!frozen() && school().licenseKind === "none"}><button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "extend-trial" })}>Test verlängern</button></Show>
                  <Show when={!frozen()}><button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "manual-license" })}>Manuelle Lizenz</button></Show>
                  <Show when={!frozen()} fallback={school().storedStatus === "suspended" ? <button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "reactivate" })}>Entsperren</button> : null}>
                    <button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "suspend" })}>Sperren</button>
                  </Show>
                </div>
              </div>

              <Show when={pending()}>
                {(action) => (
                  <form class="adm-card adm-dialog" onSubmit={submit}>
                    <h3>{TITLES[action().kind]}{action().memberName ? `: ${action().memberName}` : ""}</h3>
                    <Show when={action().kind === "suspend"}><p class="adm-sub">Alle Lehrkräfte und Schüler der Schule können sich danach nicht mehr anmelden.</p></Show>
                    <div class="adm-grid2">
                      <Show when={action().kind === "extend-trial"}>
                        <label class="adm-field wide"><span>Neues Testende</span><input name="endsOn" class="adm-input" type="date" required /></label>
                      </Show>
                      <Show when={action().kind === "manual-license"}>
                        <label class="adm-field wide"><span>Lizenz-Ende (leer = unbefristet)</span><input name="endsOn" class="adm-input" type="date" /></label>
                      </Show>
                      <Show when={action().kind === "members"}>
                        <label class="adm-field"><span>E-Mail der Lehrkraft</span><input name="email" class="adm-input" type="email" required /></label>
                        <label class="adm-field"><span>Rolle</span>
                          <select name="role" class="adm-input"><option value="teacher">Lehrkraft</option><option value="owner">Schulleitung</option></select></label>
                      </Show>
                      <label class="adm-field wide"><span>Grund (mindestens 5 Zeichen, steht im Protokoll)</span><textarea name="reason" class="adm-input" required minlength={5} maxlength={500} /></label>
                    </div>
                    <div class="adm-bar">
                      <button class={`adm-btn ${action().kind === "suspend" ? "danger" : ""}`} type="submit" disabled={saving()}>Speichern</button>
                      <button class="adm-btn ghost" type="button" onClick={() => setPending(null)}>Abbrechen</button>
                    </div>
                    <Show when={dialogError()}><p class="adm-error" role="alert">{dialogError()}</p></Show>
                  </form>
                )}
              </Show>

              <div class="adm-card">
                <div class="adm-head" style={{ "margin-bottom": "8px" }}>
                  <h3 style={{ margin: 0, "font-size": "1rem" }}>Mitglieder</h3>
                  <Show when={!frozen()}><button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "members" })}>Lehrkraft hinzufügen</button></Show>
                </div>
                <div class="adm-table-wrap">
                  <table class="adm-table">
                    <thead><tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Klassen</th><th>Konto</th><th></th></tr></thead>
                    <tbody>
                      <For each={current().members}>
                        {(member) => (
                          <tr>
                            <td>{member.firstName} {member.lastName}</td>
                            <td>{member.email}</td>
                            <td>{ROLE_LABELS[member.role]}</td>
                            <td class="num">{member.classCount}</td>
                            <td><span class={`adm-pill ${member.accountStatus}`}>{member.accountStatus === "active" ? "aktiv" : "gesperrt"}</span></td>
                            <td><button class="adm-btn ghost small" type="button" onClick={() => open({ kind: "members/remove", memberId: member.userId, memberName: `${member.firstName} ${member.lastName}` })}>Entfernen</button></td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
                <Show when={current().members.length === 0}><p class="adm-empty">Noch keine Lehrkräfte zugeordnet.</p></Show>
              </div>

              <div class="adm-card">
                <h3 style={{ margin: "0 0 8px", "font-size": "1rem" }}>Verlauf</h3>
                <div class="adm-table-wrap">
                  <table class="adm-table">
                    <thead><tr><th>Zeit</th><th>Von</th><th>Nach</th><th>Admin</th><th>Grund</th></tr></thead>
                    <tbody>
                      <For each={current().log}>
                        {(entry) => (
                          <tr>
                            <td>{formatDate(entry.createdAt)}</td>
                            <td>{entry.from ? STATUS_LABELS[entry.from] ?? entry.from : "–"}</td>
                            <td>{STATUS_LABELS[entry.to] ?? entry.to}</td>
                            <td>{entry.admin ?? "–"}</td>
                            <td>{entry.reason}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          );
        }}
      </Show>
    </section>
  );
}
